// Orders list and the order record (CONTROL V4 sd-module orders / order
// detail). The figures and the five states are the API's (services/
// order-stats.js), shared by the list and the record so the two never
// disagree. What is worked out here is only what V4 works out on the page:
// which view a row belongs to, whether it is late, and how it is sorted.

import { APPROVAL_LABEL, fmtDay, type Pallet, type Ref } from './model'
import { todayLocal } from '../timesheets/time'

export interface OrderState {
  key: string
  label: string
  tone: string
}

export interface OrderStates {
  allocation: OrderState
  preparation: OrderState
  dispatch: OrderState
  invoiced: OrderState
}

export interface OrderBoxes {
  ordered: number
  reserved: number
  prepared: number
  dispatched: number
  outstanding: number
  unallocated: number
  invoiced: number
}

export interface OrderRow {
  id: string
  order_reference: string
  customer_po: string | null
  customer_id: string | null
  customer_name: string | null
  destination_name: string | null
  requested_dispatch_date: string | null
  transport_mode: string | null
  local_export: string | null
  approval_status: string
  line_count: number
  varieties: string[]
  boxes: OrderBoxes
  states: OrderStates
  complete: boolean
  exceptions: string[]
}

export interface OrderSource {
  id: string
  variety?: string
  box_type?: string
  harvest_date?: string | null
  capacity_per_pallet?: number
  site_name?: string | null
  contractor_name?: string | null
  block_name?: string | null
  lid_id?: string | null
  lid_name?: string | null
}

export interface Reservation {
  allocation_id: string
  source: OrderSource
  boxes: number
  provisional: boolean
  reserved_at: string
}

export interface OrderLine {
  id: string
  line_number: number
  variety: string
  box_type: string
  lid: Ref | null
  instructions: string | null
  job: Ref | null
  requested: number
  reserved: number
  prepared: number
  planned: number
  dispatched: number
  outstanding: number
  unallocated: number
  lid_wrong: number
  source_short: boolean
  reservations: Reservation[]
  // Materially changed since the approved revision (reapproval only).
  changed?: boolean
  // Price / box (§7n.9): present only for a caller with the pricing
  // capability (PRICE-1); null when none has been agreed (PRICE-2).
  price_per_box?: number | null
}

export interface OrderEvent {
  id: string
  type: string
  at: string
  what: string
  by: string
  how: string
  reason: string | null
}

export interface OrderLoad {
  id: string
  reference: string
  state: string
  planned_departure: string | null
  actual_departure: string | null
  pallets: number
  boxes: number
}

export interface OrderRecord {
  order: {
    id: string
    reference: string
    customer_po: string | null
    requested_dispatch_date: string | null
    transport_mode: string | null
    local_export: string | null
    internal_notes: string | null
    // Where the fruit is finally going (188): free text beside the delivery destination.
    final_destination?: string | null
    approval_status: string
    approval_label: string
    revision: number
    approved: { by: string; at: string } | null
    submitted: { by: string; at: string } | null
    created: { by: string; at: string }
    updated: { by: string | null; at: string | null }
    return_note: string | null
    customer: (Ref & { kind?: string | null }) | null
    // What changed since the approved revision, when reapproval is required.
    changes?: { revision: number; labels: string[]; lines: string[] } | null
    destination:
      | (Ref & {
          address: string | null
          country: string | null
          port: string | null
          consignee: string | null
          instructions: string | null
        })
      | null
  }
  boxes: OrderBoxes
  states: OrderStates
  complete: boolean
  exceptions: string[]
  lines: OrderLine[]
  pallets: Pallet[]
  dispatches: OrderLoad[]
  history: OrderEvent[]
  // Shipment references (§7n.6): export orders only, outside the revision.
  freight: OrderFreight
  // Whether this caller sees prices (§7n.10 View & manage pricing, #22).
  pricing?: boolean
  // Whether this caller may approve this revision: Approve orders held, and
  // not its submitter or last material editor (D1.3). Absent from an older
  // server, when the approve route alone decides.
  approval?: OrderApproval
}

export interface OrderFreight {
  applies: boolean
  forwarder: { id: string; name: string; archived: boolean } | null
  airway_bill: string | null
  flight: string | null
  container: string | null
  updated: { by: string | null; at: string } | null
}

export interface FreightForm {
  forwarder_id: string
  airway_bill: string
  flight: string
  container: string
}

export const FREIGHT_MAX = 60

// The shipment references card (V4 order Dispatches & documents): four rows,
// '—' when empty. An archived forwarder stays shown, marked.
export function freightFacts(f: OrderFreight): { k: string; v: string }[] {
  return [
    { k: 'Freight forwarder', v: f.forwarder ? f.forwarder.name + (f.forwarder.archived ? ' (archived)' : '') : '—' },
    { k: 'Airway bill', v: f.airway_bill || '—' },
    { k: 'Flight', v: f.flight || '—' },
    { k: 'Container', v: f.container || '—' },
  ]
}

// The editor's starting values, which are also the `before` a save carries.
export function freightForm(f: OrderFreight): FreightForm {
  return {
    forwarder_id: f.forwarder?.id || '',
    airway_bill: f.airway_bill || '',
    flight: f.flight || '',
    container: f.container || '',
  }
}

// What the form would change, trimmed as the API trims; '' means cleared.
export function freightDirty(a: FreightForm, b: FreightForm): boolean {
  return (Object.keys(a) as (keyof FreightForm)[]).some((k) => a[k].trim() !== b[k].trim())
}

export function freightErrors(f: FreightForm): string[] {
  const out: string[] = []
  for (const [k, label] of [
    ['airway_bill', 'airway bill'],
    ['flight', 'flight'],
    ['container', 'container'],
  ] as const) {
    if (f[k].trim().length > FREIGHT_MAX) out.push(`Keep the ${label} to ${FREIGHT_MAX} characters.`)
  }
  return out
}

export interface CompatibleSource extends OrderSource {
  on_hand: number
  held: number
  reserved: number
  available: number
  lid_change: boolean
}

// ---------------------------------------------------------------------------
// Chips
// ---------------------------------------------------------------------------

// V4 neutralChip tones: background, ink.
export const TONE: Record<string, [string, string]> = {
  ok: ['#e8efd6', '#3f4a2c'],
  amber: ['#fdf1e8', '#8c491a'],
  warn: ['#fbe4dc', '#8a2f1d'],
  neutral: ['#f2ece1', '#5c5449'],
}

// V4 APPROVAL: the approval chip's own colours.
export const APPROVAL_TONE: Record<string, [string, string]> = {
  draft: ['#f2ece1', '#5c5449'],
  awaiting_approval: ['#fdf1e8', '#8c491a'],
  approved: ['#e8efd6', '#3f4a2c'],
  returned_for_changes: ['#fbe4dc', '#8a2f1d'],
  reapproval_required: ['#fbe4dc', '#8a2f1d'],
}

export interface Chip {
  label: string
  bg: string
  ink: string
}

const chip = (label: string, tone: string): Chip => {
  const t = TONE[tone] || TONE.neutral
  return { label, bg: t[0], ink: t[1] }
}

export function approvalChip(status: string): Chip {
  const t = APPROVAL_TONE[status] || TONE.neutral
  return { label: APPROVAL_LABEL[status] || status, bg: t[0], ink: t[1] }
}

// The four computed states as V4 colours them. Allocation is the one state
// whose "not started" is a warning: an unallocated order has nothing behind it.
// Invoiced reads "—" in the list and "Not invoiced" on the record.
export function stateChips(states: OrderStates, where: 'list' | 'record' = 'list'): Chip[] {
  const a = states.allocation
  const inv = states.invoiced
  return [
    chip(a.label, a.key === 'full' ? 'ok' : a.key === 'none' ? 'warn' : 'amber'),
    chip(states.preparation.label, states.preparation.tone),
    chip(states.dispatch.label, states.dispatch.tone),
    chip(where === 'record' && inv.key === 'none' ? 'Not invoiced' : inv.label, inv.tone),
  ]
}

// ---------------------------------------------------------------------------
// The list
// ---------------------------------------------------------------------------

export type OrdersViewKey = 'active' | 'awaiting' | 'completed' | 'all'

export const ORDER_VIEWS: [OrdersViewKey, string][] = [
  ['active', 'Active'],
  ['awaiting', 'Awaiting approval'],
  ['completed', 'Complete'],
  ['all', 'All'],
]

export type ProgressFilter = '' | 'short' | 'ready' | 'invoice'

export const PROGRESS_FILTERS: [ProgressFilter, string][] = [
  ['', 'Any progress'],
  ['short', 'With shortages'],
  ['ready', 'Prepared'],
  ['invoice', 'Uninvoiced dispatches'],
]

export const MODE_FILTERS: [string, string][] = [
  ['', 'Local and export'],
  ['local', 'Local'],
  ['export', 'Export'],
]

// Today on the tenant's clock (§9 "today" runs on the tenant operating time
// zone for every user), never the browser's calendar and never through UTC.
export function todayIso(now: Date = new Date()): string {
  return todayLocal(now)
}

const dateOf = (o: OrderRow) => (o.requested_dispatch_date || '').slice(0, 10)

// Late: dated before today with boxes still to send.
export const isOverdue = (o: OrderRow, today: string) => !!dateOf(o) && dateOf(o) < today && o.boxes.outstanding > 0
export const isUnscheduled = (o: OrderRow) => !dateOf(o)

// V4 inRange. The list reads forward from today; Complete and All drop the
// date scope. An incomplete order is work whatever its date.
export function inRange(o: OrderRow, view: OrdersViewKey, today: string): boolean {
  if (view === 'completed' || view === 'all') return true
  if (!o.complete) return true
  const d = dateOf(o)
  return !d ? true : d >= today || isOverdue(o, today)
}

export function inView(o: OrderRow, view: OrdersViewKey): boolean {
  if (view === 'all') return true
  if (view === 'awaiting') return o.approval_status === 'awaiting_approval' || o.approval_status === 'reapproval_required'
  if (view === 'completed') return o.complete
  return !o.complete
}

export interface OrderFilters {
  customer: string
  mode: string
  progress: ProgressFilter
  q: string
}

export function applyFilters(rows: OrderRow[], f: OrderFilters): OrderRow[] {
  let out = rows
  if (f.customer) out = out.filter((o) => o.customer_id === f.customer)
  if (f.mode) out = out.filter((o) => o.local_export === f.mode)
  if (f.progress === 'short') out = out.filter((o) => o.boxes.unallocated > 0)
  if (f.progress === 'ready') out = out.filter((o) => o.states.preparation.key === 'full')
  if (f.progress === 'invoice') out = out.filter((o) => o.boxes.dispatched - o.boxes.invoiced > 0)
  const q = f.q.trim().toLowerCase()
  if (q) {
    out = out.filter((o) =>
      [o.order_reference, o.customer_name || '', o.destination_name || '', o.customer_po || ''].join(' ').toLowerCase().includes(q)
    )
  }
  return out
}

// Ascending by dispatch date; undated orders last.
export const sortOrders = (rows: OrderRow[]) =>
  rows.slice().sort((a, b) => ((dateOf(a) || '9999') + a.order_reference).localeCompare((dateOf(b) || '9999') + b.order_reference))

// Each view's count under its own date scope and the same filters, so no
// tab reads a number that selecting it would contradict.
export function viewCounts(filtered: OrderRow[], today: string): Record<OrdersViewKey, number> {
  const out = {} as Record<OrdersViewKey, number>
  for (const [k] of ORDER_VIEWS) out[k] = filtered.filter((o) => inRange(o, k, today) && inView(o, k)).length
  return out
}

export function visibleOrders(all: OrderRow[], view: OrdersViewKey, f: OrderFilters, today: string): OrderRow[] {
  return sortOrders(applyFilters(all, f).filter((o) => inRange(o, view, today) && inView(o, view)))
}

const sum = (xs: OrderRow[], g: (o: OrderRow) => number) => xs.reduce((a, o) => a + g(o), 0)

export function countLine(rows: OrderRow[]): string {
  return (
    rows.length + ' order' + (rows.length === 1 ? '' : 's') + ' · ' + sum(rows, (o) => o.boxes.ordered) + ' boxes ordered · ' +
    sum(rows, (o) => o.boxes.dispatched) + ' dispatched'
  )
}

const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1)

// "Road · local": the mode, then the classification the customer carries.
export function modeText(mode: string | null, localExport: string | null): string {
  const m = mode ? cap(mode) : 'No mode'
  return localExport ? m + ' · ' + localExport : m
}

// "Gala + Fuji · 2 lines".
export function productsText(o: OrderRow): string {
  return o.varieties.join(' + ') + (o.line_count > 1 ? ' · ' + o.line_count + ' lines' : '')
}

// ord / alloc / disp: allocated counts what has already gone, as V4 does.
export const boxesTriple = (b: OrderBoxes) => b.ordered + ' / ' + (b.reserved + b.dispatched) + ' / ' + b.dispatched

export function emptyNote(total: number): string {
  return total === 0 ? 'No orders yet. Create the first order to start the daily grid.' : 'No orders match this view, range and filters.'
}

// ---------------------------------------------------------------------------
// The record
// ---------------------------------------------------------------------------

export interface OrderApproval {
  may_approve: boolean
  refusal: { error: string; code: string; reason?: 'submitter' | 'last_material_editor' } | null
}

// Why the caller cannot approve an order they submitted or last changed
// (D1.3), said before they try rather than as a refusal after.
export function selfApprovalNote(a: OrderApproval | undefined): string | null {
  if (!a || a.may_approve || a.refusal?.code !== 'SELF_APPROVAL') return null
  return a.refusal.reason === 'submitter'
    ? 'You submitted this order, so someone else with Approve orders approves it.'
    : 'You made the latest change to what this order says, so someone else with Approve orders approves it.'
}

// Which header actions the approval state offers (V4 odSubmitOn /
// odApproveOn / odReturnOn). Approve also needs the record's approval to
// allow it (D1.2, D1.3); the API refuses any move the state does not allow.
export function recordActions(status: string, approval?: OrderApproval) {
  return {
    submit: status === 'draft' || status === 'returned_for_changes',
    approve: (status === 'awaiting_approval' || status === 'reapproval_required') && (approval?.may_approve ?? true),
    ret: status === 'awaiting_approval' || status === 'reapproval_required',
  }
}

export function destinationText(d: OrderRecord['order']['destination']): string {
  if (!d || !d.address) return '—'
  return d.address + (d.country ? ', ' + d.country : '') + (d.port ? ' · port ' + d.port : '') + (d.consignee ? ' · consignee ' + d.consignee : '')
}

// V4 odApproveSummary: "150 × Gala 18kg · lid Blue".
export const approveLine = (l: OrderLine) => l.requested + ' × ' + l.variety + ' ' + l.box_type + ' · lid ' + (l.lid?.name || 'not specified')

// V4 alShort: the source is on hold, or boxes have nothing reserved behind them.
export function shortNote(l: OrderLine): string {
  if (l.source_short) return 'Reserved stock is on hold. Release the hold or reserve from another compatible group: nothing is substituted automatically.'
  if (l.unallocated > 0) return l.unallocated + ' boxes have no eligible stock reserved. Reserve from a compatible group below, or the order stays short.'
  return ''
}

export function sourceLabel(s: OrderSource): string {
  const place = s.block_name ? (s.site_name || '—') + ' · ' + s.block_name : s.site_name || '—'
  return [s.variety, s.box_type, place, s.contractor_name || '—'].filter(Boolean).join(' · ')
}

// ---------------------------------------------------------------------------
// The editor (V4 order editor). The API validates everything again and names
// every problem; what is checked here is only what V4 checks before it lets
// Save be pressed.
// ---------------------------------------------------------------------------

export interface EditorOptions {
  customers: {
    id: string
    name: string
    kind: string | null
    offered: boolean
    destinations: { id: string; name: string; country: string | null; port: string | null; offered: boolean }[]
  }[]
  varieties: string[]
  box_types: string[]
  lids: { id: string; name: string; offered: boolean }[]
  // Only with the pricing capability: the last price agreed per product.
  pricing?: boolean
  last_prices?: LastPrice[]
}

export interface LastPrice {
  variety: string
  box_type: string
  price: number
}

export interface EditorLine {
  key: string
  id: string | null
  variety: string
  box_type: string
  lid_id: string
  qty: string
  instructions: string
  dispatched: number
  // Price / box as typed; '' leaves it to the API, which stores the last
  // price for the product (§7n.9 "a line saved untouched stores the
  // prefilled figure"). `priceTouched` is whether the field was typed in:
  // an untouched price follows a product change to that product's price.
  price: string
  priceTouched: boolean
}

export interface EditorState {
  customer_id: string
  destination_id: string
  date: string
  mode: string
  po: string
  final: string
  notes: string
  lines: EditorLine[]
}

export const MODES: [string, string][] = [
  ['road', 'Road'],
  ['air', 'Air'],
  ['sea', 'Sea'],
]

let lineSeq = 0
export const blankLine = (): EditorLine => ({
  key: 'new-' + ++lineSeq,
  id: null,
  variety: '',
  box_type: '',
  lid_id: '',
  qty: '',
  instructions: '',
  dispatched: 0,
  price: '',
  priceTouched: false,
})

// V4 newOrder: dated today, by road, one empty line.
export const blankEditor = (today: string): EditorState => ({
  customer_id: '',
  destination_id: '',
  date: today,
  mode: 'road',
  po: '',
  final: '',
  notes: '',
  lines: [blankLine()],
})

// V4 toEditor: the order as it stands, each line carrying what has already
// been dispatched against it (the floor for its quantity).
export function editorFrom(rec: OrderRecord): EditorState {
  const o = rec.order
  return {
    customer_id: o.customer?.id || '',
    destination_id: o.destination?.id || '',
    date: (o.requested_dispatch_date || '').slice(0, 10),
    mode: o.transport_mode || '',
    po: o.customer_po || '',
    final: o.final_destination || '',
    notes: o.internal_notes || '',
    lines: rec.lines.map((l) => ({
      key: l.id,
      id: l.id,
      variety: l.variety,
      box_type: l.box_type,
      lid_id: l.lid?.id || '',
      qty: String(l.requested),
      instructions: l.instructions || '',
      dispatched: l.dispatched,
      price: l.price_per_box == null ? '' : l.price_per_box.toFixed(2),
      priceTouched: false,
    })),
  }
}

// ---------------------------------------------------------------------------
// Price / box (§7n.9, h 5496-5514)
// ---------------------------------------------------------------------------

export const money = (n: number) => '$' + n.toFixed(2)

// The same shape the API accepts: dollars and cents, an optional $.
export const priceValid = (p: string) => !p.trim() || /^\d{1,10}(\.\d{1,2})?$/.test(p.trim().replace(/^\$/, ''))

export function lastPriceFor(opts: EditorOptions | null, variety: string, boxType: string): number | null {
  const hit = (opts?.last_prices || []).find((p) => p.variety === variety && p.box_type === boxType)
  return hit ? hit.price : null
}

// A product change on a line whose price was not typed in clears the price,
// so the new product takes its own last price rather than the old one's.
export function withProduct(l: EditorLine, p: { variety?: string; box_type?: string }): Partial<EditorLine> {
  return l.priceTouched ? p : { ...p, price: '' }
}

// The line under the field: what an empty field will store.
export function priceHint(l: EditorLine, last: number | null): string {
  if (!priceValid(l.price)) return ''
  if (!l.variety || !l.box_type) return 'Pick a product first'
  if (last == null) return l.price.trim() ? '' : 'No earlier price for this product — enter the agreed price'
  return 'Last used ' + money(last) + ' — change if agreed otherwise'
}

export const PRICE_ERROR = 'Dollars and cents, like 38.50'

const wholeQty = (q: string) => (/^\d+$/.test(q) ? parseInt(q, 10) : NaN)

// V4 qtyErr: below what has gone is a correction, not an edit (§7n.3).
export function qtyError(l: EditorLine): string {
  const n = wholeQty(l.qty)
  if (l.id && l.dispatched > 0 && n < l.dispatched) return 'Cannot be below ' + l.dispatched + ' already dispatched: use a correction instead'
  if (l.qty !== '' && !(n > 0)) return 'Whole number of boxes'
  return ''
}

// V4 edValid and edTooSmall together: whether Save can be pressed.
export function editorValid(ed: EditorState): boolean {
  return (
    !!ed.customer_id &&
    !!ed.destination_id &&
    ed.lines.length > 0 &&
    ed.lines.every((l) => l.variety && l.box_type && wholeQty(l.qty) > 0 && !qtyError(l) && priceValid(l.price))
  )
}

export const VALID_NOTE = 'Customer, destination and at least one line with a whole box quantity are required.'

// A line can go while there is another and nothing of it has been dispatched.
// A line with stock reserved against it is refused by the API, which says so.
export const canRemoveLine = (ed: EditorState, l: EditorLine) => ed.lines.length > 1 && !(l.id && l.dispatched > 0)

export function classText(kind: string | null | undefined, chosen: boolean): string {
  if (!chosen) return ''
  if (kind === 'export') return 'Export: classification follows the customer'
  if (kind === 'local') return 'Local: classification follows the customer'
  return 'Local or export follows the customer, and this customer is not classified yet.'
}

export const MATERIAL_NOTE =
  'This order is approved. Changing customer, destination, dispatch date, transport mode, products, quantities, required lid or price saves as Reapproval required and blocks affected preparation and labels until reapproved. Booking references, the final destination and instructions do not.'

// The PUT / POST body. A line left out is removed; a line's picking job stays
// with it while its product is unchanged (the API keeps it). A price is sent
// only by a caller with the pricing capability: the API refuses one from
// anyone else, and keeps or prefills the price itself.
export function editorBody(ed: EditorState, extra: { revision?: number; approve?: boolean } = {}, pricing = false) {
  return {
    ...extra,
    customer_id: ed.customer_id,
    destination_id: ed.destination_id,
    requested_dispatch_date: ed.date || null,
    transport_mode: ed.mode || null,
    customer_po: ed.po,
    final_destination: ed.final,
    internal_notes: ed.notes,
    lines: ed.lines.map((l) => ({
      ...(l.id ? { id: l.id } : {}),
      variety: l.variety,
      box_type: l.box_type,
      required_lid_id: l.lid_id || null,
      boxes_requested: wholeQty(l.qty),
      source_instructions: l.instructions,
      ...(pricing ? { price_per_box: l.price.trim() } : {}),
    })),
  }
}

// An option list that always carries the current value, even when it is no
// longer offered, so opening an old order never silently changes it.
export function withCurrent<T extends { id: string; name: string; offered: boolean }>(xs: T[], current: string): { value: string; label: string }[] {
  return xs
    .filter((x) => x.offered || x.id === current)
    .map((x) => ({ value: x.id, label: x.name + (x.offered ? '' : ' (archived)') }))
}

export function namesWithCurrent(xs: string[], current: string): string[] {
  return current && !xs.includes(current) ? [current, ...xs] : xs
}

// The stale bar (V4 edStaleNote), from the API's 409.
export const staleNote = (by: string, revision: number) =>
  'Changed by ' + by + ' while you were editing (revision ' + revision + '). Review the newer version before saving: your edit will not overwrite it.'

// V4 odCopy: approved data only, no contractor notes, no prices.
export function summaryText(rec: OrderRecord): string {
  const o = rec.order
  return [
    o.reference + ' · ' + (o.customer?.name || '—') + ' → ' + (o.destination?.name || '—'),
    (o.requested_dispatch_date ? fmtDay(o.requested_dispatch_date) : 'Not scheduled') + ' · ' + (o.transport_mode || 'No mode'),
    ...rec.lines.map((l) => l.requested + ' × ' + l.variety + ' ' + l.box_type + (l.lid ? ' · lid ' + l.lid.name : '')),
  ].join('\n')
}
